import camelCase from 'lodash/camelCase'
import R from 'ramda'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import MaxWidth from '../../components/Layout/MaxWidth'
import OrgLinkWrapper from '../../components/OrgLinkWrapper'
import PartyInfo from '../../components/PartyInfo'
import { withTranslation } from '../../i18n'
import { orgSelector } from '../../redux/project/selectors'
import { resizeImg } from '../../utils/commonUtils'
import { getColor, getFontSize } from '../../utils/cssUtils'
import mq from '../../utils/mediaQuery'

const OrganizationInfo = ({
  t,
  organizationNo,
  showProjectsCount,
  clickOrgTracking,
  ...props
}) => {
  const organizationData = useSelector(
    state => orgSelector(state, organizationNo),
    R.equals
  )

  const {
    noOfProject,
    avatarLandscape,
    name,
    description,
    serveTargets = []
  } = organizationData

  const handleOrgLinkClick = useCallback(() => {
    clickOrgTracking(organizationNo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationNo])

  return (
    <MaxWidth>
      <StyledPartyInfo
        avatar={
          showProjectsCount ? (
            <OrgLinkWrapper
              css={`
                width: 100%;
              `}
              organizationNo={organizationNo}
              organizationName={name}
              onLinkClick={handleOrgLinkClick}
            >
              <OrganizationAvatar avatar={avatarLandscape} />
            </OrgLinkWrapper>
          ) : (
            <OrganizationAvatar avatar={avatarLandscape} />
          )
        }
        title={
          !showProjectsCount ? (
            <OrganizationName>{name}</OrganizationName>
          ) : (
            <>
              <OrganizationName>
                <OrgLinkWrapper
                  organizationNo={organizationNo}
                  organizationName={name}
                  onLinkClick={handleOrgLinkClick}
                >
                  {name}
                </OrgLinkWrapper>
              </OrganizationName>
              <OrganizationProjectsCount>
                <OrgLinkWrapper
                  organizationNo={organizationNo}
                  organizationName={name}
                >
                  {noOfProject + t('featuredProject.projects')}
                </OrgLinkWrapper>
              </OrganizationProjectsCount>
            </>
          )
        }
      >
        <IntroSectionTitle>{t('organization.intro')}</IntroSectionTitle>
        <IntroSectionContent>{description}</IntroSectionContent>
        {!R.isEmpty(serveTargets) && (
          <>
            <IntroSectionTitle>
              {t('organization.targetUser')}
            </IntroSectionTitle>
            <OrganizationCategoryTags>
              {serveTargets.map((serveTargetItemKey, i) => (
                <li key={`cat_${i}`}>
                  {t(
                    `organization.serveTarget.${camelCase(serveTargetItemKey)}`
                  )}
                </li>
              ))}
            </OrganizationCategoryTags>
          </>
        )}
      </StyledPartyInfo>
    </MaxWidth>
  )
}

export default R.compose(
  React.memo,
  withTranslation('common')
)(OrganizationInfo)

const StyledPartyInfo = styled(PartyInfo)`
  padding-bottom: 32px;
`
const OrganizationAvatar = styled.div`
  height: 80px;
  width: 100%;
  background-image: ${props =>
    props.avatar ? `url(${resizeImg(props.avatar, 320)})` : ''};
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  position: relative;
  flex-shrink: 0;
`

const OrganizationCategoryTags = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  li {
    display: inline-block;
    margin-right: 16px;

    color: ${getColor('theme')};
    line-height: 1.87;
    font-size: ${getFontSize('body2')};
    text-align: left;
    letter-spacing: 1.1px;
  }
`

const OrganizationProjectsCount = styled.div`
  margin-top: 16px;
  font-size: ${getFontSize('body2')};
  font-weight: normal;
  line-height: 1;
  color: ${getColor('grey')};
  opacity: 0.5;
  a {
    color: ${getColor('grey')};
    &:hover {
      color: ${getColor('theme')};
    }
  }
  ${mq.mobile} {
    text-align: center;
  }
`

const IntroSectionTitle = styled.div`
  font-weight: 600;
  line-height: 1.87;
  font-size: ${getFontSize('body2')};
  text-align: left;
  letter-spacing: 1.1px;
`

const IntroSectionContent = styled.div`
  line-height: 1.87;
  font-size: ${getFontSize('body2')};
  text-align: left;
  letter-spacing: 1.1px;
  margin-bottom: 12px;
`

const OrganizationName = styled.h1`
  font-size: ${getFontSize('headline')};
  width: 100%;
  line-height: 1.45;
  font-weight: 600;
  color: ${getColor('black')};
  ${mq.mobile} {
    margin: 8px auto 0 auto;
    text-align: center;
  }
  a {
    transition: color 0.1s linear;
    color: ${getColor('black')};
    &:hover {
      color: ${getColor('theme')};
    }
  }
`
